import RecommendationValue from './RecommendationValue';
import React, { useState } from 'react';
import { money } from './Portfolio/portfolioData';
import DismissPanel from './DismissPanel';
import { editableRecommendation, overrideRecommendation } from './editableRecommendation';

/**
 * One recommendation card — the `.rc` block from the design project's _cards.js,
 * ported to React. Shared by Diagnostics, Budget & pacing, Moments and
 * Recommendations.
 *
 * The shape is: headline, one-line consequence, expand for evidence. Everything
 * that used to be prose lives inside a <details>.
 *
 * NOTHING ON A CARD IS A PLATFORM HANDLE. There was a second "Technical details"
 * fold below holding the change, the entity id and the agent's routing names
 * ("Pacing run · budget · adjust_budget"). The change is the reader's first
 * question, so it moved to the top of the list; the id and the routing names
 * answer no question a media planner has, so they are gone. The id still keys
 * dismissals — it is just not printed.
 *
 * WHAT THIS COMPONENT WILL NOT DO: render a number the run did not produce. Each
 * row is omitted when its field is null rather than filled with an em-dash that
 * reads like a measured zero, and the "Expected impact" line says which of
 * confidence / priority / lift were actually scored. A card that shows an
 * invented figure is worse than a card that admits the analysis was silent.
 */

/** The lift line: "increase 27% (23–31%) conversions", or an honest absence. */
function liftText(c) {
  if (c.lp === null || c.lp === undefined) {
    // No percentage. `lm` may still hold the older HIGH/MEDIUM/LOW band.
    return c.lm ? `${c.lm} — no percentage estimate available` : 'No estimate available';
  }
  const range = (c.ll !== null && c.ll !== undefined && c.lh !== null && c.lh !== undefined)
    ? ` (${c.ll}–${c.lh}%)` : '';
  return `${c.ld ? `${c.ld} ` : ''}${c.lp}%${range}${c.lm ? ` ${c.lm}` : ''}`;
}

/** The scored figures that exist, named. Silent about the ones that do not. */
function impactText(c) {
  const parts = [];
  if (c.conf !== null && c.conf !== undefined) parts.push(`confidence ${c.conf}`);
  if (c.pri !== null && c.pri !== undefined) parts.push(`priority ${c.pri}`);
  else if (c.priBand) parts.push(`priority ${c.priBand}`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The banner above a card body.
 *
 * A turn-on card says why it was hidden. A contended card names the other
 * agent and the tab its other recommendation is on, because reading them
 * together before applying either is the whole point of computing contention.
 */
function Flag({ card: c }) {
  if (c.route === 'hidden') {
    return (
      <div className="rc-flag off">
        <span aria-hidden="true" className="material-icons">visibility_off</span>
        <span>
          Hidden by default — turning an ad group or asset group on is a structural
          decision, not an optimisation.
        </span>
      </div>
    );
  }
  if (c.conflict) {
    return (
      <div className="rc-flag conflict">
        <span aria-hidden="true" className="material-icons">warning</span>
        <span>
          <b>{c.n} recommendations target {c.tn || c.tid}</b>, with {c.nlev} different actions.
          {c.agents.length ? ` ${c.agents.join(' and ')} also weighed in` : ''}
          {c.elsewhere.length ? ', on ' : ''}
          {c.elsewhere.length ? <b>{c.elsewhere.join(' and ')}</b> : null}
          . Review the recommendations together before applying a change.
        </span>
      </div>
    );
  }
  if (c.alsoReportedBy?.length) return <div className="rc-flag dupe">
    Also found by {[...new Set(c.alsoReportedBy.map(source => source.agent))].join(' and ')}. Shown once here.
    <details><summary>Supporting analyses</summary>{c.alsoReportedBy.map(source => <p key={`${source.messageId}:${source.index}`}>
      <strong>{source.agent}: </strong>{source.plain || source.head}
    </p>)}</details>
  </div>;
  if (c.dupe) {
    return (
      <div className="rc-flag dupe">
        <span aria-hidden="true" className="material-icons">content_copy</span>
        <span>
          Also recommended by {c.agents.join(' and ') || 'another run'} — same action, same target
          {c.elsewhere.length ? <>, on <b>{c.elsewhere.join(' and ')}</b></> : null}
          . Applying once is enough.
        </span>
      </div>
    );
  }
  return null;
}

/**
 * What a dismissal MEANT, in the words the person was shown when they chose.
 * Only one of these is a rejection — the rest say the finding was correct.
 */
export const OUTCOME_LABEL = {
  rejected: 'rejected',
  deferred: 'deferred — returns',
  resolved_elsewhere: 'resolved elsewhere — check keeps running',
  constraint: 'constraint — still valid',
  unspecified: 'no reason given',
};

export const SCOPE_LABEL = {
  finding: 'Just this finding',
  campaign_check: 'This campaign, this check',
  account_check: 'This ad account, this check',
  all_campaigns_check: 'All campaigns, this check',
};

/** A <dl> row, rendered only when there is something to put in it. */
const Row = ({ label, children }) => (children === null || children === undefined || children === ''
  ? null
  : <><dt>{label}</dt><dd>{children}</dd></>);

/** Which reason set this card's kind draws from — the design's dsmKind(). */
export function dsmKind(c) {
  if (c.agent === 'Diagnostic') return 'diag';
  if (c.agent === 'Pacing') return 'pacing';
  if (c.agent === 'Moments') return 'moment';
  return 'opt';
}

/**
 * Below this, spend at risk is not worth the reader's attention.
 *
 * Deliberately low and currency-blind: these are corridor currencies (INR, AED,
 * USD, AUD) and a threshold that tried to be fair across them would need an FX
 * rate this page does not have and should not guess. The job here is only to
 * drop the rounding-error cases, not to rank.
 */
const SPEND_AT_RISK_FLOOR = 100;

/** The Apply button's own record as a chip: applying…, applied <date>, apply failed. */
function appliedChip(edit) {
  if (edit.status === 'applying') return { cls: 'rc-applied', icon: 'hourglass_top', text: 'applying…' };
  if (edit.status === 'failed') return { cls: 'rc-failed', icon: 'error_outline', text: 'apply failed' };
  const when = edit.at ? new Date(edit.at).toLocaleDateString() : null;
  return { cls: 'rc-applied', icon: 'task_alt', text: when ? `applied ${when}` : 'applied' };
}

const ID_CHIP_LIMIT = 3;

/** "Line item ID 123" / "Line item IDs 123, 456, 789 +2" for the collapsed row. */
function idChip(groups) {
  if (!groups?.length) return null;
  const parts = groups.map(({ label, ids }) => {
    const shown = ids.slice(0, ID_CHIP_LIMIT).join(', ');
    const more = ids.length > ID_CHIP_LIMIT ? ` +${ids.length - ID_CHIP_LIMIT}` : '';
    return `${label} ${ids.length === 1 ? 'ID' : 'IDs'} ${shown}${more}`;
  });
  const full = groups.map(({ label, ids }) => `${label}: ${ids.join(', ')}`).join('\n');
  return <span className="rc-ids" title={full}>{parts.join(' · ')}</span>;
}

export default function RecCard({
  card: c, dismissed = false, dismissal = null, onApply, applying = false, onDismiss,
  reasonSets = {}, notes = {}, onReinstate, applied = false, appliedEdit = null,
}) {
  const [dismissing, setDismissing] = useState(false);
  const editable = !dismissed && !applied && onApply ? editableRecommendation(c) : null;
  const [draft, setDraft] = useState(null);
  const value = draft?.source === c.raw ? draft.value : editable?.value;
  const changed = editable && value !== editable.value;
  const updated = changed ? overrideRecommendation(c, value) : c;

  // What the Apply button did to this card — the session's optimistic record
  // when given, else what /runs read back.
  const edit = appliedEdit || c.appliedEdit || null;
  // No plain_summary and no title: the run wrote no headline. The `.fb` class
  // says so under the text rather than silently showing a bare action type.
  const fallback = !c.head;
  const title = c.head || c.plain || `${c.action || 'Recommendation'} on ${c.tn || c.tid || 'this campaign'}`;
  const reason = [c.plain, c.ev, c.me].find(text => text && text !== title);

  return (
    // Collapsed by default, dismissed or applied or not — the sections read as
    // lists. A dismissed card used to force `open` so its reason, in .rc-body,
    // stayed visible; the reason now sits on the collapsed row as a chip, and
    // the outcome pill, the note and Reinstate are one click away. No `open`
    // prop at all: the browser owns the toggle, so a collapse the reader made by
    // hand is never undone by a render.
    <details
      className={`rc${c.route === 'hidden' ? ' turnon' : ''}${dismissed ? ' dismissed' : ''}${applied ? ' applied' : ''}`}
    >
      <summary className="rc-sum">
        <span className={`rc-h${fallback ? ' fb' : ''}`}>{title}</span>
        {/* consequence first, matching the design's `cons || ctx` — the
            one-liner headline_guards wrote is the better summary when it exists. */}
        <span className="rc-c">{c.cons || c.ctx || ''}</span>
        {/* What the finding puts at risk, in the campaign's own currency.
            Collapsed, a reader gets a title and a consequence — neither of which
            carries size, so "Add assets to 10 asset groups" and "Add 5 search
            themes" read as the same weight of work when one has 293,867 behind
            it and the other has a few hundred. The number is on every card
            already; it had nowhere to render. Shown only above a floor, because
            a spend-at-risk of nine is noise dressed as evidence. */}
        {/* The platform ids this acts on, so the change can be found in DV360 /
            TTD after it is applied. Long lists are cut to three here; the
            detail's id rows list every one. */}
        {idChip(c.eids)}
        {c.sar && c.sar >= SPEND_AT_RISK_FLOOR ? (
          <span className="rc-sar" title="Reported spend associated with this recommendation, not estimated loss. Expand for details.">
            {c.ccy ? money(c.sar, c.ccy, 0) : `${c.sar.toLocaleString()} (currency unknown)`} reported spend
          </span>
        ) : null}
        {/* Recurrence, computed server-side from the stored previous run of the
            same kind — never model-claimed. The detail row below quotes both
            values; the chip only says it is not the first sighting. */}
        {c.recur ? (
          <span className="rc-recur" title="The previous run of this analysis raised the same finding">
            <span aria-hidden="true" className="material-icons">repeat</span>
            also raised last run
          </span>
        ) : null}
        {/* Code-stamped (key AND value match against the applied-edit record):
            this exact change was already made through the product. The card
            still ships — verifying and reading out is the model's job — but
            the reader sees at a glance it is not new work. */}
        {c.applied ? (
          <span
            className="rc-recur rc-applied"
            title="This exact change was already applied through this product"
          >
            <span aria-hidden="true" className="material-icons">task_alt</span>
            already applied
          </span>
        ) : null}
        {/* The Apply button's own record: in progress, done on a date, or the
            last attempt failed (that card stays in the feed, so it can be tried
            again). */}
        {edit ? (() => {
          const chip = appliedChip(edit);
          return (
            <span
              className={`rc-recur ${chip.cls}`}
              title={edit.status === 'failed'
                ? (edit.error || 'The last apply failed')
                : 'Applied through this product'}
            >
              <span aria-hidden="true" className="material-icons">{chip.icon}</span>
              {chip.text}
            </span>
          );
        })() : null}
        {/* Why it was dismissed, readable without opening the card. */}
        {dismissed && dismissal ? (
          <span className="rc-recur rc-dsmchip" title="Why this card was dismissed">
            <span aria-hidden="true" className="material-icons">block</span>
            {dismissal.reason || 'no reason given'}
          </span>
        ) : null}
        {c.origin ? (
          <span className="rc-origin">
            {c.origin}{c.originDetail ? ` · ${c.originDetail}` : ''}
          </span>
        ) : null}
        <span className="rc-cmp">{c.campName || ''}</span>
        <span className="rc-act">
          {/* Not rendered at all when dismissed or applied. The design hides
              these with `.rc.dismissed .rc-btns{display:none}`, but a CSS-hidden
              button is still tabbable and still announced — so it is omitted
              instead. */}
          {(dismissed || applied) ? null : (
          <span className="rc-btns">
            {c.auto
              ? (
                <button
                  type="button"
                  className="rc-apply"
                  // The edit dispatches to a live DSP campaign, so the button
                  // has to say it is working. It gave no feedback at all while
                  // the request was in flight — the only signal was a notice at
                  // the top of the page, which on a long feed is off screen.
                  disabled={applying || !updated}
                  onClick={(e) => { e.preventDefault(); if (updated) onApply?.(updated); }}
                >
                  {applying ? 'Applying…' : 'Apply'}
                </button>
              )
              : <span className="rc-advisory">Advisory</span>}
            {/* NOTHING TO DISMISS. A card can be a STATE rather than a
                judgement — "Google is still reviewing this ad" is not a
                recommendation somebody can disagree with, it returns next run
                until the review finishes, and a dismissal would only hide the
                fact until then. The diagnosis's waiting rows set this; every
                other card is dismissible. */}
            {c.noDismiss ? null : (
            <button
              type="button"
              className="rc-dismiss"
              onClick={(e) => {
                // OPEN THE CARD, ALWAYS, AND SET THE PANEL ON — never toggle.
                //
                // The panel renders into .rc-body, a non-summary child of this
                // <details>, which the browser does not render while closed. So
                // on a collapsed card this used to flip state into hidden DOM:
                // the click did nothing visible, and a second click undid it.
                //
                // preventDefault stays. Dropping it does NOT restore the native
                // toggle — Blink and WebKit both ignore a summary click whose
                // target is a form control — so the open has to be forced.
                //
                // And it must not be a toggle: closing a <details> does not
                // unmount its children, so `dismissing` survives a manual
                // collapse. Open card, panel showing, reader collapses it, then
                // clicks Dismiss — a toggle would flip the panel OFF while this
                // handler re-opens the card, i.e. dead again.
                e.preventDefault();
                e.currentTarget.closest('details')?.setAttribute('open', '');
                setDismissing(true);
              }}
            >
              Dismiss
            </button>
            )}
          </span>
          )}
          <span aria-hidden="true" className="material-icons rc-chev">expand_more</span>
        </span>
      </summary>

      <Flag card={c} />

      <div className="rc-body">
        {/* Prepended, not appended: the reason box has to be the first thing in
            view when the card opens, or it sits below a screenful of evidence. */}
        {dismissing && !dismissed ? (
          <DismissPanel
            card={c}
            reasons={reasonSets[dsmKind(c)] || reasonSets.opt || []}
            notes={notes}
            onCancel={() => setDismissing(false)}
            onConfirm={(payload) => onDismiss?.(c, payload)}
          />
        ) : null}
        {applied && edit ? (
          <div className="dsm">
            <div className="dsm-h">
              {edit.status === 'applying' ? 'Applying' : 'Applied'}
              {edit.at ? <> — <b>{new Date(edit.at).toLocaleString()}</b></> : null}
              {edit.userName ? <> by {edit.userName}</> : null}
              <span className="dsm-req keeps">
                {edit.status === 'applying' ? 'edit run in progress' : 'applied through this product'}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--mut)', lineHeight: 1.55 }}>
              {edit.status === 'applying'
                ? 'The edit is running against the live campaign. If it fails, this card returns to the feed with the reason.'
                : 'Kept for the record — the change this card describes has been made.'}
            </div>
          </div>
        ) : null}
        {!applied && edit?.status === 'failed' ? (
          <div className="dsm">
            <div className="dsm-h">
              Last apply failed
              {edit.at ? <> — <b>{new Date(edit.at).toLocaleString()}</b></> : null}
              <span className="dsm-req">not applied</span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--red-d)', lineHeight: 1.55 }}>
              {edit.error || 'The platform did not accept the change.'} You can apply it again.
            </div>
          </div>
        ) : null}
        {dismissed && dismissal ? (
          <div className="dsm">
            <div className="dsm-h">
              Dismissed
              {dismissal.reason ? <> — <b>{dismissal.reason}</b></> : ' with no reason given'}
              <span className={`dsm-req${dismissal.outcome !== 'rejected' ? ' keeps' : ''}`}>
                {OUTCOME_LABEL[dismissal.outcome] || dismissal.outcome}
              </span>
            </div>
            {dismissal.note ? (
              <div style={{ fontSize: 12.5, color: 'var(--mut)', lineHeight: 1.55 }}>
                &ldquo;{dismissal.note}&rdquo;
              </div>
            ) : null}
            <div className="dsm-foot">
              {onReinstate ? (
                <button type="button" className="btn btn-sec btn-sm" onClick={() => onReinstate(dismissal.id)}>
                  Reinstate
                </button>
              ) : null}
              <div className="dsm-note">{SCOPE_LABEL[dismissal.scope] || dismissal.scope}
                {dismissal.expires_at
                  ? ` · returns ${new Date(dismissal.expires_at).toLocaleDateString()}`
                  : ''}</div>
            </div>
          </div>
        ) : null}
        <dl className="rc-dl">
          {/* WHAT WILL CHANGE, AND WHERE — first, and no longer buried under a
              "Technical details" fold. These two answer the reader's first
              question; everything below them is the argument for it. */}
          <Row label="The change">
            {c.cur || c.rec
              ? (
                // Stacked once either side is long enough that a side-by-side
                // pair stops reading as before/after. 48 characters is about
                // where one value starts wrapping at this measure.
                <div className={`rc-change${
                  String(c.cur ?? '').length + String(c.rec ?? '').length > 48 ? ' long' : ''}`}
                >
                  <div><strong>Current: </strong><div className="rc-v"><RecommendationValue value={c.raw?.current_value ?? c.cur}/></div></div>
                  <span className="rc-arr">&rarr;</span>
                  <strong>Recommended: </strong>
                  {editable ? <span>
                    <input type="number" aria-label="Value to apply" className="rc-v"
                      style={{ width: 110 }} min={0}
                      step={editable.integer ? 1 : 'any'} value={value} disabled={applying}
                      aria-invalid={!updated}
                      onChange={(e) => setDraft({ source: c.raw, value: e.target.value })} />
                    {changed ? <button type="button" onClick={() => setDraft(null)} disabled={applying}>Reset</button> : null}
                    {!updated ? <span role="alert">Enter a positive {editable.integer ? 'whole number' : 'number'}.</span> : null}
                    {changed && updated ? <small> Edited value; expected impact is based on the original recommendation.</small> : null}
                  </span> : <div className="rc-v"><RecommendationValue value={c.raw?.recommended_value ?? c.raw?.suggested_values ?? c.rec}/></div>}
                </div>
              )
              : null}
          </Row>
          {/* Type and name say which thing this is; the platform ids say where to
              find it, and which line items an applied change touched. */}
          <Row label="Applies to">
            {c.tt || c.tn
              ? [c.tt, c.tn ? `“${c.tn}”` : null].filter(Boolean).join(' · ')
              : null}
          </Row>
          {(c.eids || []).map(({ label, ids }) => (
            <Row key={label} label={`${label} ${ids.length === 1 ? 'ID' : 'IDs'}`}>
              <span className="rc-idlist">{ids.join(', ')}</span>
            </Row>
          ))}
          {/* An account-scoped card belongs to no one campaign; say which
              campaigns' analyses raised it instead of leaving the slot blank. */}
          <Row label="Raised in">
            {!c.camp && c.affectedCampaignNames?.length ? c.affectedCampaignNames.join(', ') : null}
          </Row>
          {/* Several Google recommendations collapsed into this card: each one,
              in its own words, one click away rather than one card each. */}
          <Row label="Customer-match list">{c.googleListName || null}</Row>
          <Row label="Recommendations">
            {c.googleItems?.length > 1 ? (
              <details className="rc-google-items">
                <summary>{c.googleType === 'REFRESH_CUSTOMER_MATCH_LIST' ? `${c.googleItems.length} customer-match lists` : `${c.googleItems.length} from Google`}</summary>
                <ul>
                  {c.googleItems.map((item, i) => (
                    <li key={item.recommendationId || i}>
                      <details className="rc-google-item">
                        <summary>{item.listName || `Google recommendation ${i + 1}`}</summary>
                        <p>{item.reasoning || item.projection || 'No explanation was returned for this recommendation.'}</p>
                        {item.reasoning && item.projection ? <p>{item.projection}</p> : null}
                      </details>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </Row>
          <Row label="Already applied">
            {c.applied
              ? (
                <>
                  Set to <span className="rc-v">{c.applied.value ?? 'this value'}</span>
                  {c.applied.at
                    ? <> on {new Date(c.applied.at).toLocaleDateString()}</> : null}
                  {' '}through this product — this card repeats that change.
                </>
              )
              : null}
          </Row>
          {/* The previous run's value for the same finding: the team reads today's
              recommendation against what was recommended last time. */}
          <Row label="Last run">
            {c.recur
              ? (
                <>
                  Recommended <span className="rc-v">{c.recur.previous_value == null ? 'no value' : <RecommendationValue value={c.recur.previous_value}/>}</span>
                  {c.recur.previous_run_at
                    ? <> on {new Date(c.recur.previous_run_at).toLocaleDateString()}</> : null}
                  ; this run {c.rec == null ? <span className="rc-v">no value</span>
                    // A structured proposal is already set out in full under The change.
                    : (c.raw?.recommended_value && typeof c.raw.recommended_value === 'object') || /^[[{]/.test(String(c.rec).trim())
                      ? 'as set out above' : <span className="rc-v">{c.rec}</span>}.
                </>
              )
              : null}
          </Row>
          <Row label="Reason">{reason}</Row>
          {/* A strategist review states its expected effect in words and never as a
              percentage it did not measure; a decision has none until it is made. */}
          <Row label="Expected impact">
            {c.strategist ? (c.strategist.kind === 'recommendation' ? (c.strategist.expectedEffect || 'Not estimated') : null) : liftText(c)}
          </Row>
          <Row label="Confidence">{c.strategist?.confidence}</Row>
          <Row label="Trade-off">{c.strategist?.tradeoff}</Row>
          <Row label="Other explanation">{c.strategist?.competing}</Row>
          <Row label="Funded by">{c.strategist?.fundedBy}</Row>
          <Row label="Frees budget for">{c.strategist?.freesBudgetFor}</Row>
          <Row label="Options">
            {c.strategist?.options?.length
              ? <ol className="rc-options">{c.strategist.options.map((option) => <li key={option}>{option}</li>)}</ol>
              : null}
          </Row>
          <Row label="Recommended if">{c.strategist?.recommendedIf}</Row>
          <Row label="Fix">{c.strategist?.fix}</Row>
          <Row label="Why now">{c.strategist?.whyNow}</Row>
          <Row label="Owner">{c.strategist?.owner}</Row>
          <Row label="Effort">{c.strategist?.effort}</Row>
        </dl>
        <details className="rc-supporting-details">
          <summary>Analysis details</summary>
          <dl className="rc-dl">
          <Row label="Supporting data">{c.ev !== reason && c.ev !== title ? c.ev : null}</Row>
          <Row label="Why this helps">{c.me !== reason && c.me !== title ? c.me : null}</Row>
          <Row label="If unchanged">{c.coi}</Row>
          <Row label="Analysis scores">{impactText(c)}</Row>
          <Row label="Spend effect">{c.strategist?.pacing}</Row>
          <Row label="How it is judged">{c.strategist?.evaluation}</Row>
          <Row label="Reverse if">{c.strategist?.reverseIf}</Row>
          <Row label="Stop rule">{c.strategist?.stopRule}</Row>
          <Row label="Account context">{c.strategist?.accountContext}</Row>
          <Row label="Reported spend">
            {c.sar && c.sar >= SPEND_AT_RISK_FLOOR ? (
              <>
                {c.ccy ? money(c.sar, c.ccy, 0) : `${c.sar.toLocaleString()} (currency unknown)`}
                {' '}spent during the analysis period, rounded to a whole currency unit.
                {' '}Uses the target’s spend when available; otherwise, the campaign’s total spend.
                {' '}This is not estimated loss or savings. Amounts can overlap across recommendations;
                do not add them together.
              </>
            ) : null}
          </Row>
          </dl>
        </details>
        <dl className="rc-dl">
          <Row label="Material at">{c.materialityPlacement}</Row>
          <Row label="Daily spend exposed">{c.exposure ? (c.exposure.daily == null ? 'Not established' : `${c.ccy || ''} ${c.exposure.daily.toLocaleString()} — exposure, not forecast loss`) : null}</Row>
          <Row label="Placement">{c.placementReason}</Row>
          <Row label="Why this matters">{c.raw?.materiality?.reason}</Row>
          <Row label="Reconsider when">{c.raw?.materiality?.promotion_trigger}</Row>
          {/* An advisory card owes the reader the reason it has no button. */}
          <Row label="Can't apply">
            {!c.auto && c.advisoryReason
              ? (
                <span className="adv-r">
                  <span aria-hidden="true" className="material-icons">block</span>
                  <span>{c.advisoryReason}</span>
                </span>
              )
              : null}
          </Row>
          <Row label="Cross-check">
            {c.judge
              ? (
                <>
                  <span className={`tag ${c.judge === 'supported' ? 'tg-grn' : 'tg-amb'}`}>
                    {c.judge}
                  </span>
                  {c.judgeReason ? <>&nbsp;{c.judgeReason}</> : null}
                </>
              )
              : null}
          </Row>
        </dl>
      </div>
    </details>
  );
}
